import type { InterventionRequest } from 'api/interventionRequestsApi';
import type { TimeWindow } from 'constants/timeWindows';
import { interventionDayRefusal, parisToday, type DayRefusal } from 'utils/interventionDate';
import type { SupportedLocale } from 'i18n';
import { centsText, parseMoneyDraft, type TaxBasis } from 'utils/money';
import type { DayWindow } from './DayWindowField';

/** The card's draft, as typed. What was saved is the same shape, and « modified » is their difference. */
export interface MissionTerms extends DayWindow {
  timeWindow: TimeWindow | null;
  indicativeTime: string;
  usefulInfo: string;
  tariffText: string;
  taxBasis: TaxBasis;
}

export const EMPTY_TERMS: MissionTerms = {
  earliest: '',
  deadline: '',
  timeWindow: null,
  indicativeTime: '',
  usefulInfo: '',
  tariffText: '',
  taxBasis: 'HT',
};

/** F15/R36 — from the newest live request, else the newest of any status, else Jira's day; the tariff is the mission's (F13). */
export const termsDraftOf = (
  source: InterventionRequest | null,
  tariff: { amountCents: number; taxBasis: TaxBasis } | null,
  jiraDay: string | null,
  locale: SupportedLocale
): MissionTerms => ({
  earliest: source?.earliestDate ?? '',
  deadline: source?.deadlineDate ?? jiraDay ?? '',
  timeWindow: source?.timeWindow ?? null,
  indicativeTime: source?.indicativeTime ?? '',
  usefulInfo: source?.customMessage ?? '',
  tariffText: tariff ? centsText(tariff.amountCents, locale) : '',
  taxBasis: tariff?.taxBasis ?? 'HT',
});

/** What the user typed before the seed landed — every field that left its empty value (CA1). */
export const typedFields = (terms: MissionTerms): Partial<MissionTerms> =>
  Object.fromEntries(
    Object.entries(terms).filter(([key, value]) => value !== EMPTY_TERMS[key as keyof MissionTerms])
  );

/** The tariff in cents, `null` when empty — and `undefined` when the text is not an amount. */
export const tariffCents = (terms: MissionTerms): number | null | undefined => parseMoneyDraft(terms.tariffText);

const tariffKey = (terms: MissionTerms): string => {
  const cents = tariffCents(terms);
  return cents === undefined ? `?${terms.tariffText}` : cents === null ? '' : `${cents} ${terms.taxBasis}`;
};

export const sameTerms = (a: MissionTerms, b: MissionTerms): boolean =>
  a.earliest === b.earliest &&
  a.deadline === b.deadline &&
  a.timeWindow === b.timeWindow &&
  a.indicativeTime === b.indicativeTime &&
  a.usefulInfo.trim() === b.usefulInfo.trim() &&
  tariffKey(a) === tariffKey(b);

export interface TermsRefusals {
  day: DayRefusal | null;
  start: boolean;
  order: boolean;
  tariff: boolean;
}

/** Said before any click (CA4); a start already saved is not blamed on whoever reopens the card. */
export const termsRefusals = (terms: MissionTerms, saved: MissionTerms, today = parisToday()): TermsRefusals => ({
  day: interventionDayRefusal(terms.deadline, today),
  start:
    terms.earliest !== '' && terms.earliest !== terms.deadline && terms.earliest !== saved.earliest && terms.earliest < today,
  order: terms.earliest !== '' && terms.deadline !== '' && terms.deadline < terms.earliest,
  tariff: tariffCents(terms) === undefined,
});
